class CrossSellingProducts extends HTMLElement {
  static LABEL = 'Cart - Most Sold With';
  static API = 'recommendations/most_sold_with';

  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.dataset.ak) {
      console.error('Cross sell products: API key is missing');
      return;
    }

    if (typeof Clerk !== 'function') {
      console.error('Cross sell products: Clerk.io SDK is not loaded');
      return;
    }

    const productIds = (this.dataset.cartItemsProductIds || '')
      .split(',')
      .filter((id) => id !== '');

    if (productIds.length === 0) return;

    const visitor = ClerkSession.getVisitorId();

    Clerk(
      'call',
      'recommendations/most_sold_with',
      {
        key: this.dataset.ak,
        limit: 4,
        attributes: ['handle'],
        exclude: [...productIds],
        products: productIds,
        labels: [CrossSellingProducts.LABEL],
        visitor: visitor || undefined,
      },
      function (response) {
        const handles = response.product_data.map((x) => x.handle);
        if (handles.length === 0) return;
        this.loadSection(handles);
      }.bind(this),
      function (response) {
        console.error('most_sold_with products: Clerk.io error:', response);
      }
    );
  }

  /**
   * Builds the section rendering URL in the current storefront locale.
   * @param {string[]} handles - Product handles from Clerk recommendations.
   * @returns {string}
   */
  getSectionRenderUrl(handles) {
    const cartUrl = window.routes?.cart_url || window.location.pathname;
    const separator = cartUrl.includes('?') ? '&' : '?';

    return `${cartUrl}${separator}sections=cross-selling-products-content&handles=${encodeURIComponent(handles.join(','))}`;
  }

  loadSection(handles) {
    fetch(this.getSectionRenderUrl(handles))
      .then((response) => response.json())
      .then((data) => {
        this.innerHTML = this.getSectionInnerHTML(data['cross-selling-products-content']);
        typeof SearchPage !== 'undefined' && SearchPage.trackClicks(this, CrossSellingProducts.LABEL, CrossSellingProducts.API);
      })
      .catch((error) => {
        console.error('Error loading the cross-selling section:', error);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('cross-selling-products', CrossSellingProducts);

class CrossSellingItemQuickAddButton extends HTMLElement {
  constructor() {
    super();
    this.crossSellingItem = this.closest('.cross-selling-item');
  }

  connectedCallback() {
    if (!this.crossSellingItem) {
      console.error('Cross selling item not found');
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartError, (cartErrorData) => {
      if (this.crossSellingItem.dataset.variantId !== cartErrorData.productVariantId) {
        return;
      }
      this.notifyError();
    });
  }

  notifyError() {
    const genericError = this.crossSellingItem.querySelector('.cross-selling-item__generic-error');

    this.crossSellingItem.classList.remove('tw-border-very-light-pink');
    this.crossSellingItem.classList.add('tw-border-montura-red');

    if (genericError) {
      genericError.classList.remove('tw-hidden');
      genericError.classList.add('tw-block');
    }
  }
}

customElements.define('cross-selling-item-quick-add-button', CrossSellingItemQuickAddButton);

class CrossSellingItemEdit extends HTMLElement {
  constructor() {
    super();
    this.crossSellingItem = this;
    this.openButton = this.querySelector('.edit-cart-open');
    this.modal = this.querySelector('#editCart');
    this.closeButton = this.querySelector('#ModalClose');
    this.sizeSelect = this.querySelector('.cart-size-select');
    this.colorSwatches = this.querySelectorAll('.color-swatch-img-wrapper');
    this.updateButton = this.querySelector('.update-button');
    this.variantInput = this.querySelector('input[name="id"]');
    this.buyButton = this.querySelector('button[name="add"]');
    this.suggestedSize = this.querySelector('.cross-selling-item__suggested-size');
    this.image = this.querySelector('.cross-selling-item__image');
    this.imageLink = this.querySelector('.cross-selling-item__image-link');
    this.price = this.querySelector('.cross-selling-item__price');
    this.productUrl = this.getLocalizedProductUrl();
    this.data = this.getData();
    this.variants = this.data.variants || [];
    this.images = this.data.images || [];
    this.sizeOptionPosition = this.data.sizeOptionPosition || 0;
    this.colorOptionPosition = this.data.colorOptionPosition || 0;
    this.activeVariant = this.getInitialVariant();
    this.pendingVariant = this.activeVariant;
    this.shouldRefreshOnClose = false;
  }

  connectedCallback() {
    if (!this.openButton || !this.modal || !this.activeVariant) {
      return;
    }

    this.openButton.addEventListener('click', this.handleOpen.bind(this));
    this.closeButton && this.closeButton.addEventListener('click', this.handleClose.bind(this));
    this.modal.addEventListener('click', this.handleModalClick.bind(this));
    this.modal.addEventListener('close', this.handleModalClose.bind(this));
    this.sizeSelect && this.sizeSelect.addEventListener('change', this.handleOptionChange.bind(this));
    for (const swatch of this.colorSwatches) {
      swatch.addEventListener('click', this.handleSwatchClick.bind(this));
    }
    this.updateButton && this.updateButton.addEventListener('click', this.handleApplyClick.bind(this));
    this.applyVariant(this.activeVariant);
    this.syncModalControls(this.activeVariant);
  }

  /**
   * Ensures the product URL includes the locale root when needed.
   * @returns {string}
   */
  getLocalizedProductUrl() {
    const productUrl = this.dataset.productUrl || '';
    const rootUrl = window.routes?.root_url || '';

    if (!productUrl || !rootUrl || rootUrl === '/') {
      return productUrl;
    }

    if (productUrl.startsWith(rootUrl)) {
      return productUrl;
    }

    return `${rootUrl}${productUrl}`;
  }

  getData() {
    const variantsData = this.querySelector('.cross-selling-item__variants');
    if (!variantsData) return {};
    return JSON.parse(variantsData.textContent);
  }

  getInitialVariant() {
    if (!this.variantInput) return null;
    return this.variants.find((variant) => `${variant.id}` === this.variantInput.value) || null;
  }

  handleOpen(event) {
    event.preventDefault();
    this.pendingVariant = this.activeVariant;
    this.shouldRefreshOnClose = false;
    this.syncModalControls(this.activeVariant);
    this.modal.showModal();
    document.body.classList.add('tw-overflow-hidden');
  }

  handleClose(event) {
    event && event.preventDefault();
    if (!this.modal.open) return;
    this.modal.classList.add('tw-opacity-0');
    this.modal.close();
  }

  async handleModalClose() {
    document.body.classList.remove('tw-overflow-hidden');
    if (!this.shouldRefreshOnClose) return;
    await this.renderPreview(this.activeVariant);
    this.shouldRefreshOnClose = false;
  }

  handleModalClick(event) {
    if (event.target === this.modal) {
      this.handleClose();
    }
  }

  handleSwatchClick(event) {
    const nextSwatch = event.target.closest('.color-swatch-img-wrapper');
    if (!nextSwatch) return;
    for (const swatch of this.colorSwatches) {
      swatch.classList.remove('tw-color-swatch--active');
    }
    nextSwatch.classList.add('tw-color-swatch--active');
    this.handleOptionChange();
  }

  handleApplyClick(event) {
    event.preventDefault();
    if (!this.pendingVariant || !this.pendingVariant.available) return;
    this.activeVariant = this.pendingVariant;
    this.shouldRefreshOnClose = true;
    this.applyVariant(this.activeVariant);
    this.handleClose();
  }

  handleOptionChange() {
    const selectedOptions = this.getSelectedOptions();
    const selectedVariant = this.findVariant(selectedOptions);
    this.pendingVariant = selectedVariant || null;
    this.updateButtonState();
  }

  getSelectedOptions() {
    const selectedOptions = { option1: '', option2: '', option3: '' };
    const selectedColor = this.getSelectedColor();

    if (this.sizeOptionPosition > 0 && this.sizeSelect) {
      selectedOptions[`option${this.sizeOptionPosition}`] = this.sizeSelect.value;
    }
    if (this.colorOptionPosition > 0 && selectedColor) {
      selectedOptions[`option${this.colorOptionPosition}`] = selectedColor;
    }

    return selectedOptions;
  }

  findVariant(selectedOptions) {
    return this.variants.find((variant) => {
      const option1Matches = !selectedOptions.option1 || this.optionsMatch(variant.option1, selectedOptions.option1);
      const option2Matches = !selectedOptions.option2 || this.optionsMatch(variant.option2, selectedOptions.option2);
      const option3Matches = !selectedOptions.option3 || this.optionsMatch(variant.option3, selectedOptions.option3);
      return option1Matches && option2Matches && option3Matches;
    });
  }

  optionsMatch(optionValue, selectedValue) {
    return `${optionValue || ''}`.toLowerCase() === `${selectedValue || ''}`.toLowerCase();
  }

  applyVariant(variant) {
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }
    if (this.crossSellingItem) {
      this.crossSellingItem.dataset.variantId = variant.id;
    }
    if (this.buyButton) {
      this.buyButton.disabled = !variant.available;
    }
    this.updateSuggestedSize(variant);
    this.updateImage(variant);
  }

  async renderPreview(variant) {
    const previewPayload = await this.fetchVariantPreview(variant.id);
    if (!previewPayload) {
      this.applyPreviewVariant(variant);
      return;
    }
    this.applyPreviewPayload(previewPayload, variant);
  }

  applyPreviewVariant(variant) {
    this.updateSuggestedSize(variant);
    this.updateImage(variant);
  }

  applyPreviewPayload(previewPayload, variant) {
    this.updateSuggestedSize(variant);

    if (previewPayload.imageSrc && this.image) {
      this.image.src = previewPayload.imageSrc;
      this.image.removeAttribute('srcset');
    }
    if (this.imageLink) {
      this.imageLink.setAttribute('aria-label', previewPayload.imageAlt || '');
    }
    previewPayload.priceMarkup && this.updatePriceMarkup(previewPayload.priceMarkup);
    this.updateFullPriceProperty(previewPayload.fullPrice);
  }

  updatePriceMarkup(priceMarkup) {
    if (!this.price) return;
    this.price.innerHTML = priceMarkup;
  }

  updateFullPriceProperty(fullPrice) {
    const form = this.querySelector('form');
    if (!form) return;

    const existingInput = form.querySelector('input[name="properties[_full_price]"]');

    if (!fullPrice) {
      existingInput && existingInput.remove();
      return;
    }

    if (existingInput) {
      existingInput.value = fullPrice;
      return;
    }

    const fullPriceInput = document.createElement('input');
    fullPriceInput.type = 'hidden';
    fullPriceInput.name = 'properties[_full_price]';
    fullPriceInput.value = fullPrice;
    form.appendChild(fullPriceInput);
  }

  async fetchVariantPreview(variantId) {
    if (!this.productUrl || !variantId) return null;

    const queryParams = new URLSearchParams({
      view: 'cross-selling-preview',
      variant: `${variantId}`,
    });
    const previewUrl = `${this.productUrl}${this.productUrl.includes('?') ? '&' : '?'}${queryParams.toString()}`;

    try {
      const response = await fetch(previewUrl);
      if (!response.ok) return null;
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const previewElement = html.querySelector('.cross-selling-preview-payload');
      if (!previewElement) return null;
      const priceElement = previewElement.querySelector('.cross-selling-preview-price');
      return {
        available: previewElement.dataset.available === 'true',
        priceMarkup: priceElement ? priceElement.innerHTML.trim() : '',
        fullPrice: previewElement.dataset.fullPrice || '',
        imageSrc: previewElement.dataset.imageSrc || '',
        imageAlt: previewElement.dataset.imageAlt || '',
      };
    } catch (error) {
      console.error('Cross-selling preview render error:', error);
      return null;
    }
  }

  syncModalControls(variant) {
    if (this.sizeSelect && this.sizeOptionPosition > 0) {
      this.sizeSelect.value = variant[`option${this.sizeOptionPosition}`] || '';
    }
    this.syncColorSwatches(variant);
    this.updateButtonState();
    this.modal.classList.remove('tw-opacity-0');
  }

  syncColorSwatches(variant) {
    if (!this.colorSwatches.length || this.colorOptionPosition < 1) return;
    const variantColor = (variant[`option${this.colorOptionPosition}`] || '').toLowerCase();
    for (const swatch of this.colorSwatches) {
      swatch.classList.remove('tw-color-swatch--active');
      if (swatch.dataset.swatchColor === variantColor) {
        swatch.classList.add('tw-color-swatch--active');
      }
    }
  }

  getSelectedColor() {
    return this.querySelector('.tw-color-swatch--active')?.dataset.swatchColor || '';
  }

  updateButtonState() {
    if (!this.updateButton) return;
    this.updateButton.disabled = !this.pendingVariant || !this.pendingVariant.available;
    this.updateButton.classList.toggle('tw-cursor-not-allowed', this.updateButton.disabled);
    this.updateButton.classList.toggle('tw-cursor-pointer', !this.updateButton.disabled);
  }

  updateSuggestedSize(variant) {
    if (!this.suggestedSize || !this.sizeOptionPosition) return;
    const sizeOptionName = `option${this.sizeOptionPosition}`;
    this.suggestedSize.textContent = variant[sizeOptionName] || '';
  }

  updateImage(variant) {
    if (!this.image) return;
    const nextImage = this.getVariantImage(variant);
    if (!nextImage || !nextImage.src) return;
    this.image.src = nextImage.src;
    this.image.removeAttribute('srcset');
    this.imageLink && this.imageLink.setAttribute('aria-label', nextImage.alt || '');
  }

  getVariantImage(variant) {
    const imageByColor = this.findImageByVariant(variant);
    if (imageByColor) return imageByColor;

    const featuredImageId = variant.featured_image?.id || variant.image_id;
    if (featuredImageId) {
      const imageById = this.images.find((image) => `${image.id}` === `${featuredImageId}`);
      if (imageById) return imageById;
    }

    if (variant.featured_image?.src) return variant.featured_image;
    return null;
  }

  findImageByVariant(variant) {
    if (!this.colorOptionPosition || !variant.sku) return null;
    const skuSplit = variant.sku.toLowerCase().split('_');
    const colorCode = skuSplit[2];
    if (!colorCode) return null;

    return this.images.find((image) => {
      if (!image.src) return false;
      const imageSrc = image.src.toLowerCase();
      if (imageSrc.includes('high') || imageSrc.includes('action')) return false;
      const imageSrcSplit = imageSrc.split('_');
      return imageSrcSplit[2] === colorCode;
    });
  }
}

customElements.define('cross-selling-item-edit', CrossSellingItemEdit);
